import { Anchor, Box, createStyles, Group, Stack } from '@mantine/core';
import { getFileExtensionConfig } from '@repo/common/utils/fileExtensionConfig';
import { Icon, IconButton, Text } from '@repo/foundations';

const useStyles = createStyles(
	(theme, { showRemove }: { showRemove: boolean }) => ({
		wrapper: {
			background: theme.other.getColor('surface/tertiary/default'),
			borderRadius: theme.radius.md,
			width: 250,
			padding: `${theme.spacing.xs} ${theme.spacing.sm}`,

			'&:first-of-type': {
				marginLeft: theme.spacing.xs,
			},
			'&:last-of-type': {
				marginRight: theme.spacing.xs,
			},
		},
		textWidth: {
			// explicitly set width to allow for text truncation
			// wrapper width - extension icon width - delete button width - Group gap - Group gap - wrapper padding left - wrapper padding right
			width: `calc(250px - 28px - ${showRemove ? 28 : 0}px - ${theme.spacing.sm} - ${theme.spacing.sm} - ${theme.spacing.sm} - ${theme.spacing.sm})`,
			textDecoration: 'none',
			color: 'inherit',

			'&:hover': {
				textDecoration: 'none',
				cursor: 'pointer',
			},
		},
		icon: {
			borderRadius: theme.radius.sm,
		},
	})
);

interface AttachmentProps {
	title: string;
	href: string;
	onRemove?: () => void;
}

export function Attachment({ title, href, onRemove }: AttachmentProps) {
	const { classes, theme } = useStyles({ showRemove: !!onRemove });

	const parts = title.split('.');
	const extension = parts.length > 1 ? parts.pop() : '';
	const extensionConfig = getFileExtensionConfig(extension ?? '');

	return (
		<Group spacing="sm" className={classes.wrapper} position="apart" noWrap>
			<Group spacing="sm" style={{ flex: 1 }} noWrap>
				<Box
					p="3xs"
					className={classes.icon}
					style={{ background: theme.other.getColor(extensionConfig.color) }}
				>
					<Icon color={extensionConfig.text} name={extensionConfig.icon} />
				</Box>
				<Anchor href={href} target="_blank" className={classes.textWidth}>
					<Stack spacing={0}>
						<Text weight="bold" size="sm" truncate>
							{title}
						</Text>
						<Text
							transform="uppercase"
							color="text/secondary/default"
							size="xs"
						>
							{extension}
						</Text>
					</Stack>
				</Anchor>
			</Group>
			{onRemove && (
				<IconButton variant="tertiary" onClick={onRemove} iconName="x" />
			)}
		</Group>
	);
}
